import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, ShieldCheck, Tag, ExternalLink } from "lucide-react";
import WhatsAppIcon from "./WhatsAppIcon";
import AdSlot from "./AdSlot";
import { WHATSAPP_URL, INSTAGRAM_URL, FACEBOOK_URL, CADASTUR_URL } from "../lib/constants";

const CADASTUR_LOGO = "https://customer-assets-39nsmqrw.emergentagent.net/job_orion-content-lab/artifacts/o3ocgnzf_cadastur-logo.webp";

export default function Footer() {
  return (
    <footer className="bg-orion-purple text-white/90 mt-24">
      {/* Footer ad slot — Leaderboard 728×90 across the top of the footer */}
      <div className="bg-white/5 py-2">
        <AdSlot id="footer" label="Publicidade · Rodapé" height={90} />
      </div>
      {/* Coupon strip */}
      <div className="bg-gradient-to-r from-[#1c0a25] via-[#2C1139] to-[#1c0a25] border-y border-orion-gold/20">
        <div className="container-orion py-6 flex flex-col md:flex-row items-center justify-between gap-5 text-center md:text-left">
          <div className="flex items-center gap-3 justify-center md:justify-start">
            <Tag size={18} className="text-orion-orange shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-orion-gold font-semibold">Viagens Orion · Cupom</div>
              <div className="font-serif text-lg md:text-xl">10% OFF na sua reserva com o cupom <span className="text-orion-orange font-semibold tracking-wider">TURISTAR</span></div>
            </div>
          </div>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="footer-coupon-cta"
            className="inline-flex items-center gap-2 bg-[#25D366] text-white px-6 py-3 text-xs uppercase tracking-widest hover:bg-[#1EBE57] transition-colors shadow-[0_8px_20px_rgba(37,211,102,0.35)] whitespace-nowrap"
          >
            <WhatsAppIcon size={16} color="#ffffff" /> Resgatar no WhatsApp
          </a>
        </div>
      </div>

      {/* Main grid — 3 symmetric columns on desktop, stacked on mobile */}
      <div className="container-orion py-16 grid md:grid-cols-3 gap-10 md:gap-12 text-center md:text-left">
        {/* Column 1 — About */}
        <div className="space-y-4">
          <div className="flex justify-center md:justify-start">
            <img src="/img/orion-logo.png" alt="Viagens Orion" loading="lazy" decoding="async" width="220" height="56" className="h-14 brightness-0 invert opacity-95" />
          </div>
          <p className="text-sm text-white/70 leading-relaxed">
            O Viagens Orion é o espaço editorial da <strong className="text-white">Viagens Orion</strong>: roteiros pensados por quem viaja, com curadoria de destinos, dicas e histórias que transformam a próxima viagem em memória para a vida toda.
          </p>
          <div className="pt-1 text-[11px] uppercase tracking-[0.28em] text-orion-gold/80">
            Sandra & Lucas Scabio · Consultores de viagem
          </div>
        </div>

        {/* Column 2 — Explore */}
        <div>
          <h3 className="text-xs uppercase tracking-[0.28em] text-orion-gold mb-4">Explorar</h3>
          <ul className="space-y-2 text-sm">
            <li><Link to="/sobre" className="hover:text-orion-orange">Sobre a Orion</Link></li>
            <li><Link to="/categoria/roteiros" className="hover:text-orion-orange">Roteiros</Link></li>
            <li><Link to="/categoria/estilos-de-viagem" className="hover:text-orion-orange">Estilos de Viagem</Link></li>
            <li><Link to="/categoria/dicas-e-cuidados" className="hover:text-orion-orange">Dicas e Cuidados</Link></li>
            <li><Link to="/cuidados" className="hover:text-orion-orange">Cuidados ao planejar</Link></li>
            <li><Link to="/politica-de-privacidade" className="hover:text-orion-orange" data-testid="footer-privacy-link">Política de Privacidade</Link></li>
            <li><Link to="/termos" className="hover:text-orion-orange" data-testid="footer-terms-link">Termos de Uso</Link></li>
          </ul>
        </div>

        {/* Column 3 — Social */}
        <div>
          <h3 className="text-xs uppercase tracking-[0.28em] text-orion-gold mb-4">Redes Sociais</h3>
          <div className="flex gap-3 mb-4 justify-center md:justify-start">
            <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" aria-label="WhatsApp" className="p-2 border border-white/20 hover:border-[#25D366] hover:text-[#25D366] transition-colors" data-testid="footer-whatsapp"><WhatsAppIcon size={16} /></a>
            <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-2 border border-white/20 hover:border-orion-orange hover:text-orion-orange transition-colors" data-testid="footer-instagram"><Instagram size={16} /></a>
            <a href={FACEBOOK_URL} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-2 border border-white/20 hover:border-orion-orange hover:text-orion-orange transition-colors" data-testid="footer-facebook"><Facebook size={16} /></a>
          </div>
          <div className="text-xs text-white/60 space-y-1">
            <div>WhatsApp: <a href={WHATSAPP_URL} target="_blank" rel="noreferrer" className="hover:text-orion-orange">+55 11 92004-3024</a></div>
            <div>Instagram: <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" className="hover:text-orion-orange">@viagensorion</a></div>
            <div>Contato: <a href="mailto:contato@viagensorion.com.br" className="hover:text-orion-orange" data-testid="footer-contact-email">contato@viagensorion.com.br</a></div>
          </div>
        </div>
      </div>

      {/* Cadastur full-width block — centered, symmetric */}
      <div className="border-t border-white/10 bg-black/20">
        <div className="container-orion py-10">
          <div
            className="mx-auto max-w-3xl border border-orion-gold/25 bg-white/[0.03] backdrop-blur-sm px-6 md:px-10 py-8 flex flex-col items-center text-center"
            data-testid="footer-legal-block"
          >
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-orion-gold font-semibold">
              <ShieldCheck size={13} /> Agência oficial · Cadastur
            </div>

            <a
              href={CADASTUR_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="Consultar Cadastur no Ministério do Turismo"
              className="mt-5 group relative inline-block bg-white rounded-2xl px-8 py-4 shadow-[0_10px_30px_rgba(0,0,0,0.25)] ring-1 ring-orion-gold/30 hover:ring-orion-orange hover:shadow-[0_14px_36px_rgba(255,106,61,0.35)] transition-all"
              data-testid="footer-cadastur-logo-btn"
            >
              <img
                src={CADASTUR_LOGO}
                alt="Cadastur — Ministério do Turismo"
                loading="lazy"
                decoding="async"
                width="220"
                height="44"
                className="h-9 md:h-11 w-auto transition-transform group-hover:scale-105"
                data-testid="footer-cadastur-logo"
              />
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-orion-purple text-orion-gold text-[9px] uppercase tracking-[0.28em] font-bold px-2.5 py-0.5 rounded-full border border-orion-gold/40 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                Verificar
              </span>
            </a>

            <dl className="mt-6 grid sm:grid-cols-3 gap-x-6 gap-y-3 text-xs text-white/75 w-full max-w-xl">
              <div>
                <dt className="text-white/45 uppercase tracking-widest text-[10px]">Razão Social</dt>
                <dd className="text-white/90 mt-1">SR SCABIO OPERADORA</dd>
              </div>
              <div>
                <dt className="text-white/45 uppercase tracking-widest text-[10px]">Nome Fantasia</dt>
                <dd className="text-white/90 mt-1">VIAGENS ORION</dd>
              </div>
              <div>
                <dt className="text-white/45 uppercase tracking-widest text-[10px]">CNPJ</dt>
                <dd className="text-white/90 font-mono tracking-wide mt-1">37.476.039/0001-90</dd>
              </div>
            </dl>

            <a
              href={CADASTUR_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-cadastur-btn"
              className="mt-7 inline-flex items-center gap-2 bg-orion-orange text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-[#E04828] transition-colors shadow-[0_10px_24px_rgba(255,106,61,0.35)]"
            >
              Consultar Cadastur no Gov <ExternalLink size={12} />
            </a>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-white/45">
              Verificação oficial no Ministério do Turismo
            </div>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container-orion py-6 text-xs text-white/50 flex flex-col md:flex-row justify-between items-center gap-3 text-center md:text-left">
          <span>© {new Date().getFullYear()} Viagens Orion. Todos os direitos reservados.</span>
          <div className="flex items-center gap-4">
            <Link to="/politica-de-privacidade" className="hover:text-white/80 transition-colors">Privacidade</Link>
            <Link to="/termos" className="hover:text-white/80 transition-colors">Termos</Link>
            <a href="mailto:contato@viagensorion.com.br" className="hover:text-white/80 transition-colors">Contato</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
